import { useCallback, useMemo } from 'react';
import { getVariables } from '@atlassian/jira-business-common/src/common/utils/get-variables/index.tsx';
import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useHierarchyStore } from '../hierarchy/hierarchy-store/index.tsx';
import { useIssuesInput } from '../use-issues-input/index.tsx';
import { useRankIssue } from '../use-rank-issue/index.tsx';

export function useRankIssueActions(issueId: string, parentIssueId?: string) {
	const cloudId = useCloudId();
	const input = useIssuesInput({
		groupIssueId: isGroupItem(parentIssueId) ? parentIssueId : undefined,
		parentIssueId: !isGroupItem(parentIssueId) ? parentIssueId : undefined,
	});
	const variables = useMemo(() => ({ ...getVariables(input), cloudId }), [cloudId, input]);
	const [{ issuesMap, hierarchyData }] = useHierarchyStore();
	const { rankIssue } = useRankIssue();
	const currentIssues = parentIssueId ? issuesMap.get(parentIssueId)?.children : hierarchyData.data;
	const currentIssueIndex = currentIssues?.findIndex((issue) => issue.issueId === Number(issueId));

	const rankItem = useCallback(
		({ oldIndex, newIndex }: { oldIndex?: number; newIndex?: number }) => {
			if (oldIndex == null || newIndex == null) return;
			if (currentIssues == null || currentIssueIndex == null || currentIssueIndex === -1) return;
			rankIssue({
				newIndex,
				oldIndex,
				issues: currentIssues,
				variables,
			});
		},
		[currentIssueIndex, currentIssues, rankIssue, variables],
	);

	const rankAfter = useCallback(() => {
		rankItem({
			oldIndex: currentIssueIndex,
			...(currentIssueIndex != null ? { newIndex: currentIssueIndex + 1 } : {}),
		});
	}, [rankItem, currentIssueIndex]);

	const rankBefore = useCallback(() => {
		rankItem({
			oldIndex: currentIssueIndex,
			...(currentIssueIndex != null ? { newIndex: currentIssueIndex - 1 } : {}),
		});
	}, [rankItem, currentIssueIndex]);

	const rankFirst = useCallback(() => {
		rankItem({
			oldIndex: currentIssueIndex,
			newIndex: 0,
		});
	}, [rankItem, currentIssueIndex]);

	const rankLast = useCallback(() => {
		rankItem({
			oldIndex: currentIssueIndex,
			...(currentIssues != null ? { newIndex: currentIssues?.length - 1 } : {}),
		});
	}, [rankItem, currentIssueIndex, currentIssues]);

	return {
		rankFirst,
		rankLast,
		rankBefore,
		rankAfter,
	};
}
